import React, { useRef, useState, useEffect } from 'react';
import Hls from 'hls.js';

export default function HLSPlayer({ url = 'https://hypr.website/hls/hypr.m3u8', currentSong = null }) {
  const audioRef = useRef(null);
  const hlsRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Update MediaSession metadata when current song changes
  useEffect(() => {
    if ('mediaSession' in navigator && isPlaying && currentSong) {
      // Get artist names from the artists object if available
      let artistName = 'HYPR Radio';
      if (currentSong.artists && Object.values(currentSong.artists).length > 0) {
        artistName = Object.values(currentSong.artists).join(', ');
      }
      
      navigator.mediaSession.metadata = new MediaMetadata({
        title: currentSong.title || 'Unknown Title',
        artist: artistName,
        album: 'HYPR Radio',
        artwork: [
          {
            src: currentSong.cover_art_url || '/hypralbum.png',
            sizes: '1536x1024',
            type: 'image/png'
          }
        ]
      });
      
      // Set action handlers for play/pause buttons on lock screen
      navigator.mediaSession.setActionHandler('play', () => {
        if (audioRef.current?.paused) {
          togglePlayback();
        }
      });
      
      navigator.mediaSession.setActionHandler('pause', () => {
        if (!audioRef.current?.paused) {
          togglePlayback();
        }
      });
    }
  }, [currentSong, isPlaying]);

  const togglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (!isInitialized) {
      if (Hls.isSupported()) {
        const hls = new Hls({
          lowLatencyMode: true,
          backBufferLength: 30,
        });
        hls.loadSource(url);
        hls.attachMedia(audio);
        hlsRef.current = hls;
      } else if (audio.canPlayType('application/vnd.apple.mpegurl')) {
        audio.src = url;
      }
      setIsInitialized(true);
    }

    if (audio.paused) {
      // Seek to live edge if possible
      const duration = audio.duration;
      if (!isNaN(duration)) {
        audio.currentTime = duration;
      }
      await audio.play();
      setIsPlaying(true);
      
      // Update MediaSession playback state
      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'playing';
        
        // Set metadata if we have current song info
        if (currentSong) {
          // Get artist names from the artists object if available
          let artistName = 'HYPR Radio';
          if (currentSong.artists && Object.values(currentSong.artists).length > 0) {
            artistName = Object.values(currentSong.artists).join(', ');
          }
          
          navigator.mediaSession.metadata = new MediaMetadata({
            title: currentSong.title || 'Unknown Title',
            artist: artistName,
            album: 'HYPR Radio',
            artwork: [
              {
                src: currentSong.cover_art_url || '/logo192.png',
                sizes: '192x192',
                type: 'image/png'
              }
            ]
          });
        }
      }
    } else {
      audio.pause();
      setIsPlaying(false);

      // Update MediaSession playback state
      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'paused';
      }

      // Destroy HLS instance and reset initialization state
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      setIsInitialized(false);
      // Optionally clear audio src for native HLS
      audio.src = '';
    }
  };

  // Cleanup HLS instance and MediaSession on unmount
  useEffect(() => {
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
      
      // Clean up MediaSession handlers
      if ('mediaSession' in navigator) {
        navigator.mediaSession.setActionHandler('play', null);
        navigator.mediaSession.setActionHandler('pause', null);
      }
    };
  }, []);

  return (
    <div className="flex flex-col gap-2 items-start bg-gray-100 p-4 rounded-lg shadow-sm">
      <div className="flex items-center gap-4">
        <button
          onClick={togglePlayback}
          className="text-xl px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center gap-2"
        >
{/*          <img
            src={isPlaying ? '/emoji/23F8.svg' : '/emoji/25B6.svg'}
            alt={isPlaying ? 'Pause' : 'Play'}
            className="inline w-5 h-5"
            draggable={false}
          />
*/}
          {isPlaying ? 'Pause' : 'Play'}
        </button>

        <div className="flex items-center gap-2 text-red-600 font-semibold">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600"></span>
          </span>
          LIVE
        </div>
      </div>

      <audio ref={audioRef} controls={false} className="hidden" />

      <div className="text-sm text-gray-600">
        Prefer a direct stream?{' '}
        <a
          href="https://hypr.website/hypr.mp3"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline hover:text-blue-800"
        >
          Stream link
        </a>
      </div>
    </div>
  );
}
